import { useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import { MapPin, Star } from "lucide-react"

import { defaultImage } from "@/lib/config"
import { Restaurant } from "@/lib/types"
import { cn, getPlaceHolderDishImage } from "@/lib/utils"
import { getCachedRestaurantUrl } from "@/lib/utils/url-cache"

import { RestoCloseStatus } from "./resto-close-status"
import { LikeButton } from "./resto-like-button"

interface RestoCardProps {
  layout?: "vertical" | "horizontal"
  liked?: boolean
  restaurant: Restaurant
  showDistance?: boolean
  fallbackImage?: string
}

export function RestoCard({
  layout,
  restaurant,
  showDistance,
  fallbackImage,
}: RestoCardProps) {
  const {
    image_compressed_web,
    image,
    name,
    address,
    latitude,
    longitude,
    distance,
    restaurant_id,
    rating,
    review_count,
    next_slot_time,
    is_closed,
    status,
    end_time,
  } = restaurant

  const isHorizontal = layout === "horizontal"
  const isVertical = layout === "vertical"

  // Memoize URL generation with caching for optimal performance
  const restaurantUrl = useMemo(() => {
    return getCachedRestaurantUrl(name, address, restaurant_id, restaurant.city)
  }, [name, address, restaurant_id, restaurant.city])

  // Memoize class names to prevent recalculation
  const { rootClass, imageWrapperClass, contentClass } = useMemo(() => {
    const rootClass = [
      "w-full rounded-lg duration-300 ease-out flex group cursor-pointer relative",
      layout
        ? isHorizontal
          ? "flex-row"
          : isVertical
            ? "flex-col"
            : ""
        : "max-md:flex-row flex-col max-md:py-5",
    ].join(" ")

    const imageWrapperClass = [
      "relative overflow-hidden rounded-xl",
      layout
        ? isHorizontal
          ? "w-[100px] h-[100px] shrink-0"
          : isVertical
            ? "aspect-[16/8] w-full"
            : ""
        : "max-md:w-[100px] max-md:h-[100px] max-md:shrink-0",
    ].join(" ")

    const contentClass = [
      "text-gray-800 flex flex-col gap-1",
      layout
        ? isHorizontal
          ? "ml-3 justify-start mt-0"
          : isVertical
            ? "mt-3"
            : ""
        : "mt-3 max-md:mt-0 max-md:ml-3 max-md:justify-start",
    ].join(" ")

    return { rootClass, imageWrapperClass, contentClass }
  }, [layout, isHorizontal, isVertical])

  // Memoize image source selection
  const imageSrc = useMemo(() => {
    return image_compressed_web || image || fallbackImage || defaultImage
  }, [image_compressed_web, image, fallbackImage])

  return (
    <div className="relative">
      <Link href={restaurantUrl}>
        <article
          className={rootClass}
          itemScope
          itemType="https://schema.org/Restaurant"
        >
          <div className={imageWrapperClass}>
            <Image
              src={imageSrc}
              alt={`Hình ảnh ${name}`}
              height={isVertical ? 320 : 200}
              width={isVertical ? 180 : 200}
              quality={95}
              placeholder="blur"
              blurDataURL={getPlaceHolderDishImage()}
              sizes="(max-width: 640px) 120px, (max-width: 1200px) 160px, 320px"
              className="object-cover w-full aspect-[16/8] max-md:aspect-square transition-transform duration-300 ease-out group-hover:scale-105"
              itemProp="image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="absolute top-1.5 left-1.5 flex-row gap-1 hidden md:flex">
              {restaurant.promo_tags.length <= 2 &&
                restaurant.promo_tags.map((tag, index) => (
                  <div key={tag.type + index}>
                    <span className="text-xs font-be-vietnam text-white bg-[rgb(222,17,53)] px-2 py-0.5 rounded-md font-medium">
                      {tag.tag}
                    </span>
                  </div>
                ))}
              {restaurant.promo_tags.length > 2 && (
                <div className="flex flex-row gap-1">
                  <span className="text-xs font-be-vietnam text-white bg-[rgb(222,17,53)] px-2 py-0.5 rounded-md font-medium">
                    {restaurant.promo_tags[0].tag}
                  </span>
                  <span className="text-xs font-be-vietnam text-white bg-[rgb(222,17,53)] px-2 py-0.5 rounded-md font-medium">
                    +{restaurant.promo_tags.length - 1} khuyến mãi
                  </span>
                </div>
              )}
            </div>

            {/* Restaurant closing status tag */}
            {is_closed !== undefined && next_slot_time && (
              <div className="absolute max-sm:inset-0 bottom-0 left-0 z-10">
                <div
                  className={cn(
                    isHorizontal && "max-md:scale-90 max-md:origin-bottom-left"
                  )}
                >
                  <RestoCloseStatus
                    isClosed={!!is_closed}
                    endTime={end_time as unknown as string}
                    nextSlotTime={next_slot_time as unknown as string}
                    status={status}
                    className="md:bg-white bg-gray-800/75 w-full max-md:text-gray-200 h-full md:w-auto md:h-auto rounded-tr-lg px-2 py-1 text-xs"
                  />
                </div>
              </div>
            )}
          </div>

          <div className={contentClass}>
            <h3
              className="text-sm font-medium line-clamp-2 leading-5 group-hover:text-primary transition-colors duration-300"
              itemProp="name"
            >
              {name}
            </h3>
            <p
              className="text-xs font-be-vietnam text-gray-600 line-clamp-1 group-hover:text-gray-800 transition-colors duration-300"
              itemProp="address"
            >
              {address}
            </p>
            <div className="flex items-center gap-1">
              {showDistance && distance && (
                <span className="text-xs font-be-vietnam text-gray-700 flex items-center gap-1 group-hover:text-gray-700 transition-colors duration-300">
                  <MapPin
                    size={12}
                    className="group-hover:text-primary transition-colors duration-300"
                    aria-hidden="true"
                  />
                  <span
                    itemProp="geo"
                    itemScope
                    itemType="https://schema.org/GeoCoordinates"
                  >
                    <meta itemProp="latitude" content={latitude?.toString()} />
                    <meta
                      itemProp="longitude"
                      content={longitude?.toString()}
                    />
                  </span>
                  {/* format to a,bb */}
                  {distance.toFixed(2)}km
                </span>
              )}

              {review_count > 10 && (
                <span className="text-xs font-be-vietnam text-gray-400 font-medium flex items-center gap-1 group-hover:text-gray-700 transition-colors duration-300">
                  <Star
                    size={12}
                    className="group-hover:text-primary transition-colors duration-300 fill-be-yellow-500 stroke-be-yellow-500"
                  />
                  <span className="text-gray-600 text-[11px]">
                    {rating} ({review_count})
                  </span>
                </span>
              )}
            </div>

            <div className="flex md:hidden gap-1 mt-1">
              {restaurant.promo_tags.length <= 2 &&
                restaurant.promo_tags.map((tag, index) => (
                  <div key={tag.type + index}>
                    <div className="text-xs font-be-vietnam bg-orange-200/40 text-orange-600 px-2 py-0.5 rounded-md font-medium flex gap-0.5 items-center">
                      <Image
                        src={tag.icon}
                        alt={tag.tag}
                        width={16}
                        height={16}
                        className="w-4 h-4"
                        loading="lazy"
                        priority={false}
                      />
                      {tag.tag}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </article>
      </Link>

      <LikeButton
        restaurantId={restaurant_id}
        compact
        className="absolute top-2 right-2 z-10 bg-white max-md:hidden"
      />
    </div>
  )
}

export function RestoCardSkeleton() {
  return (
    <div className="w-full rounded-lg flex flex-col max-md:flex-row max-md:py-5 relative animate-fadeIn">
      <div className="relative overflow-hidden rounded-lg max-md:w-[100px] max-md:h-[100px] max-md:shrink-0">
        <div className="w-full aspect-[16/8] max-md:aspect-square bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-lg" />

        {/* Status tag skeleton */}
        <div className="absolute bottom-0 left-0 z-10">
          <div className="bg-white rounded-tr-lg px-2 py-1">
            <div className="h-3 w-16 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />
          </div>
        </div>
      </div>

      <div className="mt-3 max-md:mt-0 max-md:ml-3 flex flex-col gap-1.5 max-md:justify-center max-md:flex-1 max-md:min-w-0">
        {/* Restaurant name */}
        <div className="w-full h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />

        {/* Address */}
        <div className="w-3/4 h-3.5 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />

        {/* Distance */}
        <div className="flex items-center gap-1 mt-0.5 max-md:w-full">
          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer shrink-0" />
          <div className="w-16 h-3 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md max-md:flex-1" />
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 mt-0.5 max-md:w-full">
          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer shrink-0" />
          <div className="w-20 h-3 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md max-md:flex-1" />
        </div>
      </div>

      {/* Like button skeleton */}
      <div className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white shadow-lg max-md:hidden" />
    </div>
  )
}
